import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  alpacaService,
  SentimentResponse,
  NewsArticle,
  SymbolSnapshot,
  Scorecard,
  PredictionHistoryItem,
  MarketPulse,
  MacroIntelligence,
} from '../../services/AlpacaService';
import { useAccountMode } from '../../context/AccountModeContext';
import { useToast } from '../../context/ToastContext';
import SignalLoader from '../../components/SignalLoader/SignalLoader';
import { PredictionAccuracy } from '../PredictionAccuracy/PredictionAccuracy';
import PlaybookTrackRecord from './PlaybookTrackRecord';
import styles from './Predictions.module.css';

const DEFAULT_SYMBOLS = 'SPY,QQQ,TLT,DIA,IWM';

const Predictions: React.FC = () => {
  const { mode } = useAccountMode();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [predTab, setPredTab] = useState<'brief' | 'accuracy' | 'playbook'>('brief');
  const [symbols, setSymbols] = useState(DEFAULT_SYMBOLS);
  const [sentiment, setSentiment] = useState<SentimentResponse | null>(null);
  const [snapshots, setSnapshots] = useState<Record<string, SymbolSnapshot>>({});
  const [loading, setLoading] = useState(false);
  const [snapshotLoading, setSnapshotLoading] = useState(true);
  const [error, setError] = useState('');
  const [scorecard, setScorecard] = useState<Scorecard | null>(null);
  const [history, setHistory] = useState<PredictionHistoryItem[]>([]);
  const [scoringDate, setScoringDate] = useState<string | null>(null);
  const [marketPulse, setMarketPulse] = useState<MarketPulse | null>(null);
  const [pulseLoading, setPulseLoading] = useState(true);
  const [macroData, setMacroData] = useState<MacroIntelligence | null>(null);
  const [macroLoading, setMacroLoading] = useState(true);

  const fetchSnapshots = useCallback(async () => {
    setSnapshotLoading(true);
    try {
      const data = await alpacaService.getSnapshot(symbols);
      setSnapshots(data.snapshots || {});
    } catch (err: any) {
      addToast(err.message || 'Failed to load price snapshots', 'error');
    } finally {
      setSnapshotLoading(false);
    }
  }, [symbols]);

  const fetchSentiment = useCallback(
    async (retries = 1): Promise<void> => {
      setLoading(true);
      setError('');
      try {
        const data = await alpacaService.getSentiment(symbols, '15');
        setSentiment(data);
      } catch (err: any) {
        // Retry once on 503 — cold start populates S3 cache for second attempt
        if (retries > 0 && err.message?.includes('503')) {
          console.log('[sentiment] 503 — retrying in 2s...');
          await new Promise((r) => setTimeout(r, 2000));
          return fetchSentiment(retries - 1);
        }
        setError(err.message || 'Failed to load sentiment analysis');
      } finally {
        setLoading(false);
      }
    },
    [symbols],
  );

  const fetchScorecard = useCallback(async () => {
    try {
      const [card, hist] = await Promise.all([alpacaService.getScorecard(), alpacaService.getPredictionHistory('14')]);
      setScorecard(card);
      setHistory(hist.predictions || []);
    } catch (err: any) {
      addToast(err.message || 'Failed to load scorecard', 'error');
    }
  }, []);

  const fetchMarketPulse = useCallback(async () => {
    setPulseLoading(true);
    try {
      const data = await alpacaService.getMarketPulse(mode);
      setMarketPulse(data);
    } catch (err: any) {
      addToast(err.message || 'Failed to load Market Pulse', 'error');
    } finally {
      setPulseLoading(false);
    }
  }, [mode]);

  const fetchMacro = useCallback(async () => {
    setMacroLoading(true);
    try {
      const data = await alpacaService.getMacroIntelligence();
      setMacroData(data);
    } catch (err: any) {
      // Non-critical — macro section just won't show
      console.warn('Macro intelligence fetch failed:', err.message);
    } finally {
      setMacroLoading(false);
    }
  }, []);

  const handleScoreDate = async (date: string) => {
    setScoringDate(date);
    try {
      await alpacaService.scorePrediction(date);
      await fetchScorecard(); // Refresh after scoring
    } catch (err: any) {
      addToast(err.message || 'Scoring failed', 'error');
    } finally {
      setScoringDate(null);
    }
  };

  // Load snapshots on mount
  useEffect(() => {
    fetchSnapshots();
  }, [fetchSnapshots]);

  // Auto-load sentiment + scorecard + market pulse + macro on mount
  useEffect(() => {
    fetchSentiment();
    fetchScorecard();
    fetchMarketPulse();
    fetchMacro();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefresh = () => {
    fetchSentiment();
    fetchSnapshots();
    fetchMarketPulse();
    fetchMacro();
  };

  const handleSymbolsKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleRefresh();
    }
  };

  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHrs = Math.floor(diffMin / 60);
    if (diffHrs < 24) return `${diffHrs}h ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const moodPosition = sentiment ? Math.max(0, Math.min(100, (sentiment.mood_score + 100) / 2)) : 50;

  const moodClass = (mood: string) => {
    switch (mood) {
      case 'bullish':
        return styles.moodBullish;
      case 'bearish':
        return styles.moodBearish;
      case 'mixed':
        return styles.moodMixed;
      default:
        return styles.moodNeutral;
    }
  };

  const moodEmoji = (mood: string) => {
    switch (mood) {
      case 'bullish':
        return '📈';
      case 'bearish':
        return '📉';
      case 'mixed':
        return '↔️';
      default:
        return '➡️';
    }
  };

  const vixColor = (level: string) => {
    switch (level) {
      case 'calm':
        return '#27ae60';
      case 'normal':
        return '#2c3e50';
      case 'elevated':
        return '#f39c12';
      case 'high':
        return '#e67e22';
      case 'extreme':
        return '#c0392b';
      default:
        return '#888';
    }
  };

  const pcrLabel = (label: string) => label.replace(/_/g, ' ').toUpperCase();

  return (
    <section className={styles.page}>
      <h1 className={styles.title}>🔮 Pre-Market Brief</h1>
      <p className={styles.subtitle}>AI-powered sentiment analysis &bull; News &amp; market data</p>

      <div className={styles.tabBar}>
        <button className={predTab === 'brief' ? styles.tabActive : styles.tab} onClick={() => setPredTab('brief')}>
          Brief
        </button>
        <button
          className={predTab === 'accuracy' ? styles.tabActive : styles.tab}
          onClick={() => setPredTab('accuracy')}
        >
          Accuracy
        </button>
        <button
          className={predTab === 'playbook' ? styles.tabActive : styles.tab}
          onClick={() => setPredTab('playbook')}
        >
          Playbook Track Record
        </button>
      </div>

      {predTab === 'accuracy' && <PredictionAccuracy />}
      {predTab === 'playbook' && <PlaybookTrackRecord />}

      {predTab === 'brief' && (
        <>
          {/* Controls */}
          <div className={styles.controls}>
            <input
              type="text"
              className={styles.symbolInput}
              value={symbols}
              onChange={(e) => setSymbols(e.target.value.toUpperCase())}
              onKeyDown={handleSymbolsKeyDown}
              placeholder="Symbols (comma separated)"
              title="Prediction symbols (comma separated)"
            />
            <button className={styles.refreshBtn} onClick={handleRefresh} disabled={loading}>
              {loading ? '⏳ Analyzing...' : '🔄 Refresh Analysis'}
            </button>
            {sentiment?.analyzed_at && (
              <span className={styles.timestamp}>Last analyzed: {formatTime(sentiment.analyzed_at)}</span>
            )}
          </div>

          {/* Snapshots */}
          <div className={styles.snapshotSection}>
            <h2 className={styles.sectionTitle}>📊 Market Snapshot</h2>
            {snapshotLoading ? (
              <SignalLoader message="Loading prices..." size="small" />
            ) : (
              <div className={styles.snapshotGrid}>
                {Object.entries(snapshots).map(([sym, snap]) => (
                  <div key={sym} className={styles.snapCard}>
                    <span className={styles.snapSymbol}>{sym}</span>
                    <span className={styles.snapPrice}>${snap.price?.toFixed(2) || '—'}</span>
                    <span className={`${styles.snapChange} ${snap.change >= 0 ? styles.positive : styles.negative}`}>
                      {snap.change >= 0 ? '+' : ''}
                      {snap.change?.toFixed(2)} ({snap.change_pct >= 0 ? '+' : ''}
                      {snap.change_pct?.toFixed(2)}%)
                    </span>
                    {snap.volume != null && (
                      <span className={styles.snapMeta}>Vol: {(snap.volume / 1000000).toFixed(1)}M</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Market Pulse Indicators */}
          <div className={styles.pulseSection}>
            <h2 className={styles.sectionTitle}>🫀 Market Pulse</h2>
            {pulseLoading ? (
              <SignalLoader message="Loading indicators..." size="small" />
            ) : marketPulse ? (
              <div className={styles.pulseGrid}>
                {/* VIX */}
                <div className={styles.pulseCard}>
                  <span className={styles.pulseLabel}>VIX</span>
                  {marketPulse.vix?.value != null ? (
                    <>
                      <span className={styles.pulseValue} style={{ color: vixColor(marketPulse.vix.level) }}>
                        {marketPulse.vix.value.toFixed(2)}
                      </span>
                      {marketPulse.vix.prev_close > 0 && marketPulse.vix.prev_close !== marketPulse.vix.value && (
                        <span
                          className={`${styles.pulseChange} ${
                            marketPulse.vix.change >= 0 ? styles.negative : styles.positive
                          }`}
                        >
                          {marketPulse.vix.change >= 0 ? '+' : ''}
                          {marketPulse.vix.change.toFixed(2)} ({marketPulse.vix.change_pct >= 0 ? '+' : ''}
                          {marketPulse.vix.change_pct.toFixed(1)}%)
                        </span>
                      )}
                      <span className={styles.pulseMeta}>{marketPulse.vix.level.toUpperCase()}</span>
                    </>
                  ) : (
                    <span className={styles.pulseMeta}>Unavailable</span>
                  )}
                </div>

                {/* Put/Call Ratio */}
                <div className={styles.pulseCard}>
                  <span className={styles.pulseLabel}>Put/Call Ratio</span>
                  {marketPulse.put_call_ratio?.pcr != null ? (
                    <>
                      <span className={styles.pulseValue}>{marketPulse.put_call_ratio?.pcr.toFixed(2)}</span>
                      <span
                        className={`${styles.pulseBadge} ${
                          marketPulse.put_call_ratio?.pcr > 1.0
                            ? styles.badgeBearish
                            : marketPulse.put_call_ratio?.pcr < 0.7
                              ? styles.badgeBullish
                              : styles.badgeNeutral
                        }`}
                      >
                        {pcrLabel(marketPulse.put_call_ratio?.label)}
                      </span>
                      <span className={styles.pulseMeta}>
                        Put OI: {(marketPulse.put_call_ratio?.put_oi / 1000000).toFixed(2)}M &bull; Call OI:{' '}
                        {(marketPulse.put_call_ratio?.call_oi / 1000000).toFixed(2)}M
                      </span>
                    </>
                  ) : (
                    <span className={styles.pulseMeta}>Unavailable</span>
                  )}
                </div>

                {/* Short Volume */}
                <div className={styles.pulseCard}>
                  <span className={styles.pulseLabel}>Short Volume (SPY)</span>
                  {marketPulse.short_volume?.short_ratio != null ? (
                    <>
                      <span className={styles.pulseValue}>
                        {marketPulse.short_volume?.short_ratio_pct?.toFixed(1)}%
                      </span>
                      <span
                        className={`${styles.pulseBadge} ${
                          (marketPulse.short_volume?.short_ratio ?? 0) > 0.5
                            ? styles.badgeBearish
                            : (marketPulse.short_volume?.short_ratio ?? 0) < 0.35
                              ? styles.badgeBullish
                              : styles.badgeNeutral
                        }`}
                      >
                        {marketPulse.short_volume?.label?.toUpperCase() ?? 'N/A'}
                      </span>
                      <span className={styles.pulseMeta}>
                        {((marketPulse.short_volume?.short_volume ?? 0) / 1000000).toFixed(1)}M of{' '}
                        {((marketPulse.short_volume?.total_volume ?? 0) / 1000000).toFixed(1)}M total
                      </span>
                    </>
                  ) : (
                    <span className={styles.pulseMeta}>Unavailable</span>
                  )}
                </div>
              </div>
            ) : null}
            {marketPulse?.fetched_at && (
              <span className={styles.pulseTimestamp}>Updated: {formatTime(marketPulse.fetched_at)}</span>
            )}
          </div>

          {/* Geopolitical & Macro Intelligence */}
          <div className={styles.geoSection}>
            <h2 className={styles.sectionTitle}>🌍 Geopolitical &amp; Macro</h2>
            {macroLoading ? (
              <SignalLoader message="Loading macro intelligence..." size="small" />
            ) : macroData ? (
              <>
                {/* Oil Prices */}
                {macroData.oil && !macroData.oil.error && (
                  <div className={styles.oilRow}>
                    {(['wti', 'brent'] as const).map((name) => {
                      const d = macroData.oil[name];
                      if (!d || d.error) return null;
                      return (
                        <div key={name} className={styles.oilCard}>
                          <span className={styles.oilLabel}>{name.toUpperCase()} Crude</span>
                          <span className={styles.oilPrice}>${d.price.toFixed(2)}</span>
                          <span className={`${styles.oilChange} ${d.change >= 0 ? styles.positive : styles.negative}`}>
                            {d.change >= 0 ? '+' : ''}
                            {d.change.toFixed(2)} ({d.change_pct >= 0 ? '+' : ''}
                            {d.change_pct.toFixed(1)}%)
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Geopolitical Risk Gauge (from sentiment analysis) */}
                {sentiment && sentiment.geopolitical_risk > 0 && (
                  <div className={styles.geoRisk}>
                    <div className={styles.geoRiskHeader}>
                      <span className={styles.geoRiskLabel}>Geopolitical Risk</span>
                      <span
                        className={`${styles.geoRiskScore} ${
                          sentiment.geopolitical_risk >= 75
                            ? styles.riskCritical
                            : sentiment.geopolitical_risk >= 50
                              ? styles.riskHigh
                              : sentiment.geopolitical_risk >= 25
                                ? styles.riskElevated
                                : styles.riskLow
                        }`}
                      >
                        {sentiment.geopolitical_risk}/100
                      </span>
                    </div>
                    <div className={styles.geoRiskBar}>
                      <div
                        className={`${styles.geoRiskFill} ${
                          sentiment.geopolitical_risk >= 75
                            ? styles.riskCritical
                            : sentiment.geopolitical_risk >= 50
                              ? styles.riskHigh
                              : sentiment.geopolitical_risk >= 25
                                ? styles.riskElevated
                                : styles.riskLow
                        }`}
                        style={{ width: `${sentiment.geopolitical_risk}%` }}
                      />
                    </div>
                    <div className={styles.geoRiskScale}>
                      <span>Low</span>
                      <span>Elevated</span>
                      <span>High</span>
                      <span>Critical</span>
                    </div>
                    {sentiment.geopolitical_summary && (
                      <p className={styles.geoRiskSummary}>{sentiment.geopolitical_summary}</p>
                    )}
                  </div>
                )}

                {/* Sector Implications */}
                {sentiment?.sector_implications && sentiment.sector_implications.length > 0 && (
                  <div className={styles.sectorSection}>
                    <h3 className={styles.sectorTitle}>Sector Implications</h3>
                    <div className={styles.sectorGrid}>
                      {sentiment.sector_implications.map((s, i) => (
                        <div key={i} className={styles.sectorCard}>
                          <span className={styles.sectorName}>{s.sector}</span>
                          <span
                            className={`${styles.sectorBias} ${s.bias === 'bullish' ? styles.positive : styles.negative}`}
                          >
                            {s.bias === 'bullish' ? '▲' : '▼'} {s.bias}
                          </span>
                          <span className={styles.sectorReason}>{s.reason}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Trading Playbook */}
                {sentiment?.trading_playbook && sentiment.trading_playbook.length > 0 && (
                  <div className={styles.playbookSection}>
                    <h3 className={styles.sectorTitle}>Trading Playbook</h3>
                    <div className={styles.playbookGrid}>
                      {sentiment.trading_playbook.map((trade, i) => (
                        <div
                          key={i}
                          className={`${styles.playbookCard} ${trade.direction === 'long' ? styles.playbookLong : styles.playbookShort}`}
                        >
                          <div className={styles.playbookHeader}>
                            <span className={styles.playbookTicker}>{trade.ticker}</span>
                            <span
                              className={`${styles.playbookDirection} ${trade.direction === 'long' ? styles.positive : styles.negative}`}
                            >
                              {trade.direction === 'long' ? '▲ LONG' : '▼ SHORT'}
                            </span>
                          </div>
                          <p className={styles.playbookThesis}>{trade.thesis}</p>
                          <div className={styles.playbookMeta}>
                            <span
                              className={`${styles.playbookConviction} ${
                                trade.conviction === 'high'
                                  ? styles.convictionHigh
                                  : trade.conviction === 'medium'
                                    ? styles.convictionMed
                                    : styles.convictionLow
                              }`}
                            >
                              {trade.conviction}
                            </span>
                            <span className={styles.playbookTimeframe}>
                              {trade.timeframe === 'day_trade'
                                ? 'Day Trade'
                                : trade.timeframe === 'swing'
                                  ? 'Swing (1-5d)'
                                  : 'Position (1-4w)'}
                            </span>
                          </div>
                          <button
                            className={styles.playbookTradeBtn}
                            onClick={() =>
                              navigate(
                                `/quick-trade?action=${trade.direction === 'long' ? 'buy' : 'short'}&symbol=${trade.ticker}`,
                              )
                            }
                          >
                            Trade {trade.ticker}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* GDELT Events */}
                {macroData.gdelt?.events?.length > 0 && (
                  <div className={styles.gdeltSection}>
                    <h3 className={styles.gdeltTitle}>
                      Geopolitical Events
                      {macroData.gdelt?.top_themes.length > 0 && (
                        <span className={styles.gdeltThemes}>
                          {macroData.gdelt?.top_themes.slice(0, 3).map((t) => (
                            <span key={t} className={styles.gdeltTheme}>
                              {t}
                            </span>
                          ))}
                        </span>
                      )}
                    </h3>
                    <div className={styles.gdeltList}>
                      {macroData.gdelt?.events.slice(0, 8).map((ev, i) => (
                        <a
                          key={i}
                          href={ev.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.gdeltEvent}
                        >
                          <span className={styles.gdeltHeadline}>{ev.headline}</span>
                          <span className={styles.gdeltSource}>{ev.source}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* FRED Macro Indicators */}
                {macroData.macro && !macroData.macro?.error && (
                  <div className={styles.macroRow}>
                    {macroData.macro.cpi?.value != null && (
                      <div className={styles.macroCard}>
                        <span className={styles.macroLabel}>CPI</span>
                        <span className={styles.macroValue}>{macroData.macro.cpi.value.toFixed(1)}</span>
                        <span className={styles.macroDate}>{macroData.macro.cpi.date}</span>
                      </div>
                    )}
                    {macroData.macro.yield_curve?.value != null && (
                      <div className={styles.macroCard}>
                        <span className={styles.macroLabel}>Yield Curve (10Y-2Y)</span>
                        <span
                          className={`${styles.macroValue} ${macroData.macro.yield_curve.value < 0 ? styles.negative : styles.positive}`}
                        >
                          {macroData.macro.yield_curve.value.toFixed(2)}%
                        </span>
                        <span className={styles.macroMeta}>
                          {macroData.macro.yield_curve.value < 0 ? 'Inverted' : 'Normal'}
                        </span>
                      </div>
                    )}
                    {macroData.macro.usd_index?.value != null && (
                      <div className={styles.macroCard}>
                        <span className={styles.macroLabel}>USD Index</span>
                        <span className={styles.macroValue}>{macroData.macro.usd_index.value.toFixed(2)}</span>
                        <span
                          className={`${styles.macroChange} ${(macroData.macro.usd_index.change ?? 0) >= 0 ? styles.positive : styles.negative}`}
                        >
                          {(macroData.macro.usd_index.change ?? 0) >= 0 ? '+' : ''}
                          {(macroData.macro.usd_index.change ?? 0).toFixed(2)}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Shipping & Chokepoints */}
                {macroData.shipping?.chokepoints && Object.keys(macroData.shipping.chokepoints).length > 0 && (
                  <div className={styles.shippingSection}>
                    <h3 className={styles.sectorTitle}>Maritime Chokepoints</h3>
                    <div className={styles.shippingGrid}>
                      {Object.entries(macroData.shipping.chokepoints).map(([id, cp]) => {
                        if (cp.status === 'no_data' || cp.status === 'error') return null;
                        const statusColor =
                          cp.status === 'critically_low'
                            ? styles.shippingCritical
                            : cp.status === 'below_normal'
                              ? styles.shippingBelow
                              : cp.status === 'above_normal'
                                ? styles.shippingAbove
                                : styles.shippingNormal;
                        return (
                          <div key={id} className={`${styles.shippingCard} ${statusColor}`}>
                            <span className={styles.shippingLabel}>{cp.label}</span>
                            <div className={styles.shippingStats}>
                              <span className={styles.shippingCount}>{cp.current_vessels}</span>
                              <span className={styles.shippingUnit}>vessels</span>
                            </div>
                            <div className={styles.shippingDetail}>
                              <span>{cp.tankers} tankers</span>
                              <span className={`${(cp.delta_pct ?? 0) < 0 ? styles.negative : styles.positive}`}>
                                {(cp.delta_pct ?? 0) >= 0 ? '+' : ''}
                                {cp.delta_pct?.toFixed(0)}% vs avg
                              </span>
                            </div>
                            {cp.avg_30d != null && cp.avg_30d > 0 && (
                              <div className={styles.shippingBar}>
                                <div
                                  className={styles.shippingBarFill}
                                  style={{ width: `${Math.min(100, ((cp.current_vessels ?? 0) / cp.avg_30d) * 100)}%` }}
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {macroData.fetched_at && (
                  <span className={styles.pulseTimestamp}>Macro data: {formatTime(macroData.fetched_at)}</span>
                )}
              </>
            ) : null}
          </div>

          {/* Sentiment loading / error */}
          {loading && !sentiment && <SignalLoader message="Analyzing headlines..." size="small" />}
          {error && <p className={styles.error}>⚠️ {error}</p>}

          {/* Mood Gauge */}
          {sentiment && (
            <>
              <div className={styles.moodSection}>
                <div className={styles.moodHeader}>
                  <span className={styles.moodLabel}>Market Mood</span>
                  <span className={`${styles.moodBadge} ${moodClass(sentiment.mood)}`}>
                    {moodEmoji(sentiment.mood)} {sentiment.mood}
                    {sentiment.mood_score !== 0 && ` (${sentiment.mood_score > 0 ? '+' : ''}${sentiment.mood_score})`}
                  </span>
                </div>
                <div className={styles.moodBar}>
                  <div className={styles.moodIndicator} style={{ left: `${moodPosition}%` }} />
                </div>
                <div className={styles.moodScale}>
                  <span>Bearish</span>
                  <span>Neutral</span>
                  <span>Bullish</span>
                </div>
                <p className={styles.moodSummary}>{sentiment.summary}</p>
              </div>

              {/* Risks & Catalysts */}
              {(sentiment.key_risks?.length > 0 || sentiment.key_catalysts?.length > 0) && (
                <div className={styles.insightsRow}>
                  <div className={styles.insightCard}>
                    <h3 className={`${styles.insightTitle} ${styles.riskTitle}`}>⚠️ Key Risks</h3>
                    <ul className={styles.insightList}>
                      {sentiment.key_risks?.map((risk, i) => (
                        <li key={i} className={styles.riskItem}>
                          {risk}
                        </li>
                      ))}
                      {(!sentiment.key_risks || sentiment.key_risks.length === 0) && (
                        <li>No significant risks identified</li>
                      )}
                    </ul>
                  </div>
                  <div className={styles.insightCard}>
                    <h3 className={`${styles.insightTitle} ${styles.catalystTitle}`}>🚀 Key Catalysts</h3>
                    <ul className={styles.insightList}>
                      {sentiment.key_catalysts?.map((cat, i) => (
                        <li key={i} className={styles.catalystItem}>
                          {cat}
                        </li>
                      ))}
                      {(!sentiment.key_catalysts || sentiment.key_catalysts.length === 0) && (
                        <li>No significant catalysts identified</li>
                      )}
                    </ul>
                  </div>
                </div>
              )}

              {/* Headlines with sentiment */}
              <div className={styles.newsSection}>
                <h2 className={styles.sectionTitle}>📰 Headlines &amp; Sentiment</h2>
                {sentiment.articles?.map((article: NewsArticle, i: number) => (
                  <div
                    key={article.id || i}
                    className={`${styles.headline} ${
                      article.impact === 'high'
                        ? styles.impactHigh
                        : article.impact === 'medium'
                          ? styles.impactMedium
                          : styles.impactLow
                    }`}
                  >
                    <div
                      className={`${styles.sentimentDot} ${
                        article.sentiment === 'bullish'
                          ? styles.dotBullish
                          : article.sentiment === 'bearish'
                            ? styles.dotBearish
                            : styles.dotNeutral
                      }`}
                    />
                    <div className={styles.headlineContent}>
                      <p className={styles.headlineText}>
                        <a href={article.url} target="_blank" rel="noopener noreferrer">
                          {article.headline}
                        </a>
                      </p>
                      <div className={styles.headlineMeta}>
                        <span className={styles.headlineSource}>{article.source}</span>
                        <span className={styles.headlineTime}>{formatTime(article.created_at)}</span>
                        {article.symbols?.length > 0 && (
                          <div className={styles.symbolTags}>
                            {article.symbols.map((s) => (
                              <span key={s} className={styles.symbolTag}>
                                {s}
                              </span>
                            ))}
                          </div>
                        )}
                        {article.brief && <span className={styles.headlineBrief}>{article.brief}</span>}
                      </div>
                    </div>
                    <span
                      className={`${styles.sentimentBadge} ${
                        article.sentiment === 'bullish'
                          ? styles.badgeBullish
                          : article.sentiment === 'bearish'
                            ? styles.badgeBearish
                            : styles.badgeNeutral
                      }`}
                    >
                      {article.sentiment}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Prediction Scorecard */}
          <div className={styles.scorecardSection}>
            <h2 className={styles.sectionTitle}>🏆 Prediction Scorecard</h2>

            {scorecard && scorecard.total > 0 && (
              <div className={styles.scorecardGrid}>
                <div className={styles.scoreCard}>
                  <span className={styles.scoreLabel}>Accuracy</span>
                  <span
                    className={`${styles.scoreValue} ${scorecard.accuracy_pct >= 60 ? styles.positive : scorecard.accuracy_pct < 40 ? styles.negative : ''}`}
                  >
                    {scorecard.accuracy_pct}%
                  </span>
                </div>
                <div className={styles.scoreCard}>
                  <span className={styles.scoreLabel}>Predictions</span>
                  <span className={styles.scoreValue}>{scorecard.total}</span>
                </div>
                <div className={styles.scoreCard}>
                  <span className={styles.scoreLabel}>Correct</span>
                  <span className={`${styles.scoreValue} ${styles.positive}`}>{scorecard.correct}</span>
                </div>
                <div className={styles.scoreCard}>
                  <span className={styles.scoreLabel}>Streak</span>
                  <span
                    className={`${styles.scoreValue} ${scorecard.streak_type === 'correct' ? styles.positive : styles.negative}`}
                  >
                    {scorecard.current_streak} {scorecard.streak_type === 'correct' ? '✓' : '✗'}
                  </span>
                </div>
                <div className={styles.scoreCard}>
                  <span className={styles.scoreLabel}>Avg Error</span>
                  <span className={styles.scoreValue}>{scorecard.avg_magnitude_error}</span>
                </div>
                {Object.entries(scorecard.grades)
                  .sort()
                  .map(([grade, count]) => (
                    <div key={grade} className={styles.scoreCard}>
                      <span className={styles.scoreLabel}>Grade {grade}</span>
                      <span
                        className={`${styles.scoreValue} ${
                          grade === 'A'
                            ? styles.gradeA
                            : grade === 'B'
                              ? styles.gradeB
                              : grade === 'C'
                                ? styles.gradeC
                                : grade === 'D'
                                  ? styles.gradeD
                                  : styles.gradeF
                        }`}
                      >
                        {count}
                      </span>
                    </div>
                  ))}
              </div>
            )}

            {history.length > 0 && (
              <div style={{ overflowX: 'auto', width: '100%' }}>
                <table className={styles.historyTable}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Predicted</th>
                      <th>Score</th>
                      <th>Actual SPY</th>
                      <th>Direction</th>
                      <th>Grade</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((pred) => (
                      <tr key={pred.date}>
                        <td>{pred.date}</td>
                        <td>
                          <span
                            className={`${pred.mood_score > 0 ? styles.positive : pred.mood_score < 0 ? styles.negative : ''}`}
                          >
                            {pred.mood} ({pred.mood_score > 0 ? '+' : ''}
                            {pred.mood_score})
                          </span>
                        </td>
                        <td>{pred.bias || '—'}</td>
                        {pred.score ? (
                          <>
                            <td className={pred.score.actual_change >= 0 ? styles.positive : styles.negative}>
                              {pred.score.actual_change >= 0 ? '+' : ''}
                              {pred.score.actual_change_pct.toFixed(2)}%
                            </td>
                            <td>{pred.score.direction_correct ? '✅' : '❌'}</td>
                            <td>
                              <span
                                className={`${styles.gradeBadge} ${
                                  pred.score.grade === 'A'
                                    ? styles.gradeBgA
                                    : pred.score.grade === 'B'
                                      ? styles.gradeBgB
                                      : pred.score.grade === 'C'
                                        ? styles.gradeBgC
                                        : pred.score.grade === 'D'
                                          ? styles.gradeBgD
                                          : styles.gradeBgF
                                }`}
                              >
                                {pred.score.grade}
                              </span>
                            </td>
                            <td></td>
                          </>
                        ) : (
                          <>
                            <td>
                              <span className={styles.pendingBadge}>pending</span>
                            </td>
                            <td>—</td>
                            <td>—</td>
                            <td>
                              <button
                                className={styles.scoreBtn}
                                onClick={() => handleScoreDate(pred.date)}
                                disabled={scoringDate === pred.date}
                              >
                                {scoringDate === pred.date ? '...' : 'Score'}
                              </button>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {(!history || history.length === 0) && (
              <p style={{ color: '#999', fontSize: 14, textAlign: 'center', padding: '20px 0' }}>
                Predictions will appear here after your first analysis. Click "Refresh Analysis" above to get started!
              </p>
            )}
          </div>
        </>
      )}

      <Link to="/home" className={styles.back}>
        ← Back to Home
      </Link>
    </section>
  );
};

export default Predictions;
